import { getAuthToken } from '../api/client';
import { ApiError, unwrap } from '../api/envelope';
import { config } from '../config';

/**
 * The console end of the dashboard → new-tab sign-in handoff.
 *
 * The dashboard cannot share its Firebase session with us — different origin,
 * so different IndexedDB — which is why clicking Channels used to land on
 * /signin. Instead it trades the ID token it already holds at the CRM for a
 * one-time code and opens `/sso?code=…&to=…`. We swap that code for a Firebase
 * custom token and sign in for real, after which this console owns a
 * self-refreshing session and needs nothing further from the dashboard tab.
 *
 * `captureHandoff` runs in main.tsx BEFORE the router mounts, mirroring
 * captureOauthReturn: it parks the code and strips it from the address bar, so
 * a single-use credential never survives in history or in a shared link. The
 * parsing core is pure and unit-tested; only the shell touches window.
 */

export interface Handoff {
  code: string;
  /** Where to land afterwards — always an internal path, never a URL. */
  to: string;
}

export interface ParsedHandoff {
  handoff: Handoff | null;
  /** The URL to replaceState to, when params had to be stripped. */
  cleanedUrl: string | null;
}

/** 32 bytes of base64url, as minted by the CRM's services/handoff.js. */
export const HANDOFF_CODE = /^[A-Za-z0-9_-]{43}$/;

/**
 * The landing path is attacker-supplied (it rides in the URL the dashboard
 * opens), so it is only ever accepted as a path on THIS origin: one leading
 * slash, and never `//` or `/\` — both of which browsers read as a
 * protocol-relative URL and would turn this into an open redirect.
 */
export function safeReturnPath(raw: string | null): string {
  if (!raw || !raw.startsWith('/')) return '/';
  if (raw.startsWith('//') || raw.startsWith('/\\')) return '/';
  // /sso would loop back into a code that has already been spent.
  if (raw === '/sso' || raw.startsWith('/sso?') || raw.startsWith('/sso/')) return '/';
  return raw;
}

/**
 * The link a minted code goes into. Pure, and the only place that builds it —
 * `to` is percent-encoded here so a config path with its own query (or a bare
 * `/`) cannot bleed into the outer one.
 */
export function handoffUrl(origin: string, code: string, to: string): string {
  return `${origin}/sso?code=${encodeURIComponent(code)}&to=${encodeURIComponent(to)}`;
}

/** Pure: does this search string carry a handoff, and what's the clean URL. */
export function parseHandoff(search: string, pathname: string, hash: string): ParsedHandoff {
  const params = new URLSearchParams(search);
  const code = params.get('code');
  if (code === null) return { handoff: null, cleanedUrl: null };

  const to = safeReturnPath(params.get('to'));
  params.delete('code');
  params.delete('to');
  const rest = params.toString();
  return {
    // A malformed code is parked as null rather than sent: the round trip would
    // only come back as the same expired-link screen.
    handoff: HANDOFF_CODE.test(code) ? { code, to } : null,
    cleanedUrl: pathname + (rest ? `?${rest}` : '') + hash,
  };
}

// ------------------------------------------------------- window-touching shell

let captured: Handoff | null = null;

/** Call once, before the router mounts. */
export function captureHandoff(loc: Location): void {
  const { handoff, cleanedUrl } = parseHandoff(loc.search, loc.pathname, loc.hash);
  captured = handoff;
  if (cleanedUrl !== null) {
    window.history.replaceState(null, '', cleanedUrl);
  }
}

/** Non-destructive read — safe under StrictMode double renders. */
export function peekHandoff(): Handoff | null {
  return captured;
}

export function clearHandoff(): void {
  captured = null;
}

// ------------------------------------------------------------------- the call

export interface HandoffSession {
  /** A Firebase custom token — feed it to signInWithCustomToken, nothing else. */
  token: string;
  tenant: string;
  email: string | null;
}

/**
 * Mint a code for a tab we are about to open. Used by the /embed iframe, which
 * holds no session of its own but can always ask the dashboard for a token —
 * so the config tab it opens arrives signed in rather than at the password
 * form.
 *
 * Not on the request() seam: that one targets `${crmBase}/api/v1`, and the
 * handoff endpoints sit outside it. `getAuthToken` is, though — reading
 * `auth.currentUser` here instead would be null inside the iframe.
 */
export async function issueHandoff(): Promise<string> {
  const { token, tenant } = await getAuthToken(false);
  let response: Response;
  try {
    response = await fetch(`${config.crmBase}/auth/handoff`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        Tenant: tenant,
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: '{}',
      signal: AbortSignal.timeout(15_000),
    });
  } catch {
    throw new ApiError('network', 0, 'Could not reach the sign-in server.');
  }
  const { code } = unwrap<{ code: string }>(await response.json());
  if (!HANDOFF_CODE.test(code ?? '')) {
    throw new ApiError('badShape', 0, 'The server did not return a usable sign-in code.');
  }
  return code;
}

/**
 * Deliberately NOT on the api/client seam: that one attaches an Authorization
 * header and a tenant, and this is the call made precisely because we have
 * neither yet.
 */
export async function redeemHandoff(code: string): Promise<HandoffSession> {
  let response: Response;
  try {
    response = await fetch(`${config.crmBase}/auth/handoff/redeem`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ code }),
      signal: AbortSignal.timeout(30_000),
    });
  } catch {
    throw new ApiError('network', 0, 'Could not reach the sign-in server. Check your connection.');
  }

  let json: unknown;
  try {
    json = await response.json();
  } catch {
    throw new ApiError('badShape', 0, 'Unexpected response from the server.');
  }

  const session = unwrap<HandoffSession>(json);
  if (!session?.token) {
    throw new ApiError('badShape', 0, 'The sign-in link could not be completed.');
  }
  return session;
}
